import React, { useEffect, useState } from 'react';
import { NotebookPen, Layers, Search } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { useProject } from '@/lib/ProjectContext';
import NotesPanel from '@/components/notes/NotesPanel';

const KEY = 'stagehead.notes.sel';

// One place for every note: your private notepad, everything the team has written, or one project's notes.
export default function Notes() {
  const { projects = [] } = useProject() || {};
  const [sel, setSelState] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY)) || { kind: 'pad' }; } catch { return { kind: 'pad' }; } });
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const setSel = (s) => { setSelState(s); try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* private mode */ } };
  const project = sel.kind === 'project' ? projects.find((p) => p._id === sel.id) : null;
  const btn = (on) => `w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm text-left whitespace-nowrap ${on ? 'bg-[#1E2A33] text-white' : 'text-[#33404A] hover:bg-[#F3EFE7]'}`;

  return (
    <Layout title="Notes">
      <div className="flex flex-col md:flex-row gap-4">
        <aside className="md:w-56 shrink-0">
          <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible pb-1">
            <button className={btn(sel.kind === 'pad')} onClick={() => setSel({ kind: 'pad' })}><NotebookPen className="w-4 h-4" />My notepad</button>
            <button className={btn(sel.kind === 'all')} onClick={() => setSel({ kind: 'all' })}><Layers className="w-4 h-4" />All notes</button>
            {projects.length > 0 && <div className="hidden md:block text-[11px] uppercase tracking-wider font-semibold text-[#94A3B8] px-2.5 pt-3 pb-1">Projects</div>}
            {projects.map((p) => (
              <button key={p._id} className={btn(sel.kind === 'project' && sel.id === p._id)} onClick={() => setSel({ kind: 'project', id: p._id })}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color || '#A62B1F' }} /><span className="truncate">{p.name}</span>
              </button>
            ))}
          </div>
        </aside>
        <div className="flex-1 min-w-0 max-w-3xl">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h1 className="text-xl font-semibold text-[#1E2A33] mr-auto" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
              {sel.kind === 'pad' ? 'My notepad' : sel.kind === 'all' ? 'All notes' : project?.name || 'Project'}
            </h1>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes" className="h-9 pl-8 pr-3 rounded-md border border-[#E3DDD1] text-sm outline-none focus:border-[#1E2A33] w-48" />
            </div>
          </div>
          <p className="text-xs text-[#5C6670] mb-3">
            {sel.kind === 'pad' ? 'Private: only you see these.' : sel.kind === 'all' ? 'Your notes and the notes your team shares on projects and contacts.' : 'Shared with the team on this project unless you make one private.'}
            {' '}Start a line with [ ] and it can become a task.
          </p>
          <NotesPanel key={`${sel.kind}-${sel.id || ''}`} header={false} showWhere={sel.kind !== 'project'} q={debounced}
            scope={sel.kind === 'pad' ? 'private' : 'all'} projectId={project?._id}
            emptyText={debounced ? 'No notes match that.' : sel.kind === 'pad' ? 'Your notepad is empty. Jot something down.' : 'No notes yet.'} />
        </div>
      </div>
    </Layout>
  );
}
